import { useState } from "react";

import {
    FaPlay,
    FaCheckCircle,
    FaTimesCircle,
    FaCoins,
    FaChartLine,
} from "react-icons/fa";

import AdminPage from "./components/AdminPage";
import AdminCard from "./components/AdminCard";
import AdminTable from "./components/AdminTable";
import PrimaryButton from "./components/PrimaryButton";

import { runBacktest } from "./api/backtestApi";


export default function BacktestValue() {

    const [from, setFrom] =
        useState("");

    const [to, setTo] =
        useState("");

    const [maxMatches, setMaxMatches] =
        useState(100);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const [result, setResult] =
        useState(null);


    /*
     * ---------------------------------------------------------
     * RUN BACKTEST
     * ---------------------------------------------------------
     */

    const executeBacktest = async () => {

        if (!from || !to) {

            setError(
                "Please select both a start and end date."
            );

            return;

        }


        setLoading(true);
        setError("");
        setResult(null);


        try {

            const data =
                await runBacktest({

                    from,

                    to,

                    maxMatches:
                        Number(maxMatches),

                    onlyValueBets:
                        true

                });


            setResult(data);

        } catch (err) {

            console.error(
                "Value backtest error:",
                err
            );

            setError(
                err?.response?.data?.message ||
                "Failed to run value backtest."
            );

        } finally {

            setLoading(false);

        }

    };


    /*
     * ---------------------------------------------------------
     * EXTRACT VALUE BETS
     * ---------------------------------------------------------
     *
     * The backend returns valueBets inside each match.
     *
     * We flatten them so the table can display:
     *
     * Match
     * Market
     * Odd
     * Probability
     * Margin
     * Confidence
     * Result
     *
     * ---------------------------------------------------------
     */

    const valueBetRows = [];


    if (result?.results) {

        for (
            const item
            of result.results
        ) {

            const bets =
                item.prediction
                    ?.valueBets || [];


            for (
                const bet
                of bets
            ) {

                valueBetRows.push({

                    ...bet,

                    matchId:
                        item.match?.id,

                    date:
                        item.match?.date,

                    home:
                        item.match?.home,

                    away:
                        item.match?.away,

                    actual:
                        item.actual,

                });

            }

        }

    }


    /*
     * ---------------------------------------------------------
     * HELPERS
     * ---------------------------------------------------------
     */

    const formatDate = (date) => {

        if (!date)
            return "-";


        return new Date(date).toLocaleString(
            "en-GB",
            {
                dateStyle: "short",
                timeStyle: "short"
            }
        );

    };


    const formatMarket = (market) => {

        if (!market)
            return "-";


        return market
            .replaceAll("_", " ")
            .toUpperCase();

    };


    const isBetWon = (bet) => {

        if (
            bet.won !== undefined
        ) {

            return bet.won;

        }


        /*
         * Fallback for the normal 1X2 markets.
         */

        if (
            bet.market === "home_win"
        ) {

            return (
                bet.actual?.result ===
                "home_win"
            );

        }


        if (
            bet.market === "draw"
        ) {

            return (
                bet.actual?.result ===
                "draw"
            );

        }


        if (
            bet.market === "away_win"
        ) {

            return (
                bet.actual?.result ===
                "away_win"
            );

        }


        /*
         * For goal markets we need
         * actual goals.
         */

        const totalGoals =
            Number(
                bet.actual?.homeGoals || 0
            ) +
            Number(
                bet.actual?.awayGoals || 0
            );


        if (
            bet.market === "over_15"
        ) {

            return totalGoals > 1.5;

        }


        if (
            bet.market === "over_25"
        ) {

            return totalGoals > 2.5;

        }


        if (
            bet.market === "over_35"
        ) {

            return totalGoals > 3.5;

        }


        if (
            bet.market === "btts"
        ) {

            return (
                Number(
                    bet.actual?.homeGoals || 0
                ) > 0 &&
                Number(
                    bet.actual?.awayGoals || 0
                ) > 0
            );

        }


        return null;

    };


    /*
     * Calculate result locally if the
     * backend hasn't added "won" yet.
     */

    const processedValueBets =
        valueBetRows.map(
            (bet) => ({

                ...bet,

                calculatedWon:
                    isBetWon(bet)

            })
        );


    /*
     * ---------------------------------------------------------
     * SUMMARY
     * ---------------------------------------------------------
     */

    const totalBets =
        processedValueBets.length;


    const wonBets =
        processedValueBets.filter(
            bet =>
                bet.calculatedWon === true
        ).length;


    const lostBets =
        processedValueBets.filter(
            bet =>
                bet.calculatedWon === false
        ).length;


    const pendingBets =
        processedValueBets.filter(
            bet =>
                bet.calculatedWon === null
        ).length;


    const winRate =
        totalBets > 0
            ? (
                wonBets /
                totalBets *
                100
            )
            : 0;


    /*
     * Flat €1 stake.
     */

    let profit = 0;


    for (
        const bet
        of processedValueBets
    ) {

        if (
            bet.calculatedWon === true
        ) {

            profit +=
                Number(bet.odd || 0) - 1;

        }

        else if (
            bet.calculatedWon === false
        ) {

            profit -= 1;

        }

    }


    const roi =
        totalBets > 0
            ? (
                profit /
                totalBets *
                100
            )
            : 0;


    /*
     * ---------------------------------------------------------
     * RENDER
     * ---------------------------------------------------------
     */

    return (

        <AdminPage
            title="Backtest Value"
            subtitle="Backtest only the value bets identified by the prediction engine."
        >


            {/* ================================================= */}
            {/* CONTROLS */}
            {/* ================================================= */}

            <AdminCard className="p-6 mb-6">

                <div
                    className="
                        grid
                        grid-cols-1
                        md:grid-cols-4
                        gap-4
                        items-end
                    "
                >

                    {/* FROM */}

                    <div>

                        <label
                            className="
                                block
                                text-sm
                                text-gray-400
                                mb-2
                            "
                        >
                            From
                        </label>

                        <input
                            type="date"
                            value={from}
                            onChange={(e) =>
                                setFrom(e.target.value)
                            }
                            className="
                                w-full
                                bg-black/30
                                border
                                border-green-500/20
                                rounded-lg
                                px-4
                                py-3
                                text-white
                                focus:outline-none
                                focus:border-green-400
                            "
                        />

                    </div>


                    {/* TO */}

                    <div>

                        <label
                            className="
                                block
                                text-sm
                                text-gray-400
                                mb-2
                            "
                        >
                            To
                        </label>

                        <input
                            type="date"
                            value={to}
                            onChange={(e) =>
                                setTo(e.target.value)
                            }
                            className="
                                w-full
                                bg-black/30
                                border
                                border-green-500/20
                                rounded-lg
                                px-4
                                py-3
                                text-white
                                focus:outline-none
                                focus:border-green-400
                            "
                        />

                    </div>


                    {/* MAX MATCHES */}

                    <div>

                        <label
                            className="
                                block
                                text-sm
                                text-gray-400
                                mb-2
                            "
                        >
                            Max Matches
                        </label>

                        <input
                            type="number"
                            min="1"
                            max="10000"
                            value={maxMatches}
                            onChange={(e) =>
                                setMaxMatches(
                                    e.target.value
                                )
                            }
                            className="
                                w-full
                                bg-black/30
                                border
                                border-green-500/20
                                rounded-lg
                                px-4
                                py-3
                                text-white
                                focus:outline-none
                                focus:border-green-400
                            "
                        />

                    </div>


                    {/* RUN */}

                    <PrimaryButton
                        onClick={executeBacktest}
                        disabled={loading}
                    >

                        <div
                            className="
                                flex
                                items-center
                                justify-center
                                gap-2
                            "
                        >

                            <FaPlay
                                size={13}
                            />

                            {loading
                                ? "Running..."
                                : "Run Value Backtest"
                            }

                        </div>

                    </PrimaryButton>

                </div>


                {error && (

                    <div
                        className="
                            mt-4
                            rounded-lg
                            bg-red-900/30
                            border
                            border-red-500/20
                            text-red-400
                            px-4
                            py-3
                        "
                    >

                        {error}

                    </div>

                )}

            </AdminCard>


            {result && (

                <>


                    {/* ================================================= */}
                    {/* SUMMARY CARDS */}
                    {/* ================================================= */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            sm:grid-cols-2
                            lg:grid-cols-5
                            gap-4
                            mb-6
                        "
                    >

                        {/* VALUE BETS */}

                        <AdminCard className="p-5">

                            <p
                                className="
                                    text-gray-400
                                    text-sm
                                "
                            >
                                Value Bets
                            </p>

                            <p
                                className="
                                    text-3xl
                                    font-bold
                                    text-white
                                    mt-1
                                "
                            >
                                {totalBets}
                            </p>

                        </AdminCard>


                        {/* WON */}

                        <AdminCard className="p-5">

                            <p
                                className="
                                    text-gray-400
                                    text-sm
                                "
                            >
                                Won
                            </p>

                            <p
                                className="
                                    text-3xl
                                    font-bold
                                    text-green-400
                                    mt-1
                                "
                            >
                                {wonBets}
                            </p>

                        </AdminCard>


                        {/* LOST */}

                        <AdminCard className="p-5">

                            <p
                                className="
                                    text-gray-400
                                    text-sm
                                "
                            >
                                Lost
                            </p>

                            <p
                                className="
                                    text-3xl
                                    font-bold
                                    text-red-400
                                    mt-1
                                "
                            >
                                {lostBets}
                            </p>

                        </AdminCard>


                        {/* WIN RATE */}

                        <AdminCard className="p-5">

                            <p
                                className="
                                    text-gray-400
                                    text-sm
                                "
                            >
                                Win Rate
                            </p>

                            <p
                                className="
                                    text-3xl
                                    font-bold
                                    text-green-400
                                    mt-1
                                "
                            >
                                {winRate.toFixed(2)}%
                            </p>

                        </AdminCard>


                        {/* ROI */}

                        <AdminCard className="p-5">

                            <div
                                className="
                                    flex
                                    items-center
                                    justify-between
                                "
                            >

                                <div>

                                    <p
                                        className="
                                            text-gray-400
                                            text-sm
                                        "
                                    >
                                        ROI
                                    </p>

                                    <p
                                        className={`
                                            text-3xl
                                            font-bold
                                            mt-1
                                            ${
                                                roi >= 0
                                                    ? "text-green-400"
                                                    : "text-red-400"
                                            }
                                        `}
                                    >
                                        {roi.toFixed(2)}%
                                    </p>

                                </div>

                                <FaCoins
                                    className="
                                        text-yellow-400
                                        text-xl
                                    "
                                />

                            </div>

                        </AdminCard>

                    </div>


                    {/* ================================================= */}
                    {/* PERIOD */}
                    {/* ================================================= */}

                    <AdminCard className="p-5 mb-6">

                        <div
                            className="
                                flex
                                flex-col
                                md:flex-row
                                md:justify-between
                                md:items-center
                                gap-3
                            "
                        >

                            <div>

                                <p
                                    className="
                                        text-sm
                                        text-gray-400
                                    "
                                >
                                    Backtest Period
                                </p>

                                <p
                                    className="
                                        text-white
                                        font-semibold
                                        mt-1
                                    "
                                >
                                    {result.period?.from}
                                    {" "}
                                    →{" "}
                                    {result.period?.to}
                                </p>

                            </div>


                            <div
                                className="
                                    text-sm
                                    text-gray-400
                                "
                            >

                                Matches tested:
                                {" "}
                                <span
                                    className="
                                        text-white
                                        font-semibold
                                    "
                                >
                                    {result.matches?.processed ??
                                        result.matches?.total ??
                                        0}
                                </span>

                            </div>

                        </div>

                    </AdminCard>


                    {/* ================================================= */}
                    {/* VALUE BET TABLE */}
                    {/* ================================================= */}

                    <AdminTable
                        headers={[
                            "Match",
                            "Date",
                            "Market",
                            "Probability",
                            "Odd",
                            "Margin",
                            "Confidence",
                            "Result",
                        ]}
                        loading={false}
                        empty={
                            processedValueBets.length === 0
                        }
                        emptyMessage="No value bets found for this period."
                    >

                        {processedValueBets.map(
                            (bet, index) => {

                                const won =
                                    bet.calculatedWon;


                                return (

                                    <tr
                                        key={`
                                            ${bet.matchId}-
                                            ${bet.market}-
                                            ${index}
                                        `}
                                        className="
                                            border-t
                                            border-green-500/10
                                            hover:bg-white/5
                                            transition
                                        "
                                    >

                                        {/* MATCH */}

                                        <td className="p-4">

                                            <div
                                                className="
                                                    text-white
                                                    font-medium
                                                "
                                            >
                                                {bet.home}
                                            </div>

                                            <div
                                                className="
                                                    text-gray-500
                                                    text-sm
                                                "
                                            >
                                                {bet.away}
                                            </div>

                                        </td>


                                        {/* DATE */}

                                        <td
                                            className="
                                                p-4
                                                text-gray-400
                                                whitespace-nowrap
                                            "
                                        >
                                            {formatDate(
                                                bet.date
                                            )}
                                        </td>


                                        {/* MARKET */}

                                        <td className="p-4">

                                            <span
                                                className="
                                                    px-3
                                                    py-1
                                                    rounded-full
                                                    text-xs
                                                    bg-green-900/30
                                                    text-green-400
                                                    font-medium
                                                "
                                            >
                                                {formatMarket(
                                                    bet.market
                                                )}
                                            </span>

                                        </td>


                                        {/* PROBABILITY */}

                                        <td
                                            className="
                                                p-4
                                                text-white
                                                font-semibold
                                            "
                                        >
                                            {bet.probability}%
                                        </td>


                                        {/* ODD */}

                                        <td
                                            className="
                                                p-4
                                                text-yellow-400
                                                font-bold
                                            "
                                        >
                                            {bet.odd}
                                        </td>


                                        {/* MARGIN */}

                                        <td
                                            className="
                                                p-4
                                                text-green-400
                                                font-semibold
                                            "
                                        >
                                            {bet.margin}%
                                        </td>


                                        {/* CONFIDENCE */}

                                        <td
                                            className="
                                                p-4
                                                text-gray-300
                                            "
                                        >
                                            {bet.confidenceScore != null
                                                ? `${bet.confidenceScore}%`
                                                : "-"}
                                        </td>


                                        {/* RESULT */}

                                        <td className="p-4">

                                            {won === true ? (

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        text-green-400
                                                    "
                                                >

                                                    <FaCheckCircle />

                                                    Won

                                                </div>

                                            ) : won === false ? (

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        text-red-400
                                                    "
                                                >

                                                    <FaTimesCircle />

                                                    Lost

                                                </div>

                                            ) : (

                                                <span
                                                    className="
                                                        text-gray-500
                                                    "
                                                >
                                                    Pending
                                                </span>

                                            )}

                                        </td>

                                    </tr>

                                );

                            }
                        )}

                    </AdminTable>

                </>

            )}

        </AdminPage>

    );

}